import React from 'react';
import { ArrowLeft, ArrowRight, Check, Play, Zap, Shield, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';

const LandingPage = () => {
  const features = [
    { icon: <Zap size={32} />, title: 'Lightning Fast', description: 'Optimized for speed and performance' },
    { icon: <Shield size={32} />, title: 'Secure', description: 'Enterprise-grade security' },
    { icon: <Globe size={32} />, title: 'Global', description: 'Available worldwide' },
  ];

  const benefits = [
    'Increase productivity by 300%',
    'Save 10+ hours per week',
    'Automate repetitive tasks',
    'Real-time collaboration',
    'Advanced analytics',
    '24/7 customer support',
  ];

  return (
    <div className="min-h-screen bg-white pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/portfolio"
          className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft size={20} />
          Back to Portfolio
        </Link>

        {/* Hero Section */}
        <section className="py-20">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-primary-100 text-primary-700 rounded-full text-sm font-semibold mb-6">
              🚀 New: AI-Powered Features
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent">
              Transform Your Workflow
            </h1>
            <p className="text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The all-in-one platform that helps teams collaborate, automate, and achieve more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2">
                Start Free Trial
                <ArrowRight size={20} />
              </button>
              <button className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold text-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                <Play size={20} />
                Watch Demo
              </button>
            </div>
          </div>

          {/* Hero Image/Visual */}
          <div className="bg-gradient-to-br from-primary-500 via-purple-500 to-pink-500 rounded-2xl p-1">
            <div className="bg-white rounded-xl p-12 text-center">
              <div className="text-6xl mb-4">📊</div>
              <p className="text-gray-600">Dashboard Preview</p>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-gray-50 rounded-2xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us?</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to succeed, all in one place
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg text-center">
                <div className="text-primary-600 mb-4 flex justify-center">{feature.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Everything you need to grow your business
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Join thousands of companies already using our platform to streamline their operations.
              </p>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="bg-primary-100 p-2 rounded-lg">
                      <Check className="text-primary-600" size={20} />
                    </div>
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
              <button className="mt-8 px-8 py-4 bg-primary-600 text-white rounded-lg font-semibold text-lg hover:bg-primary-700 transition-colors">
                Get Started Today
              </button>
            </div>
            <div className="bg-gradient-to-br from-primary-100 to-purple-100 rounded-2xl p-12 text-center">
              <div className="text-8xl mb-4">📈</div>
              <p className="text-gray-600 text-lg">Visual Analytics</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary-600 to-purple-600 rounded-2xl text-white text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to get started?</h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join thousands of teams already using our platform to achieve their goals.
          </p>
          <button className="px-8 py-4 bg-white text-primary-600 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors">
            Start Free Trial
          </button>
        </section>
      </div>
    </div>
  );
};

export default LandingPage;

